import { useEffect, useState } from 'react';

import { AVAILABLE_THEMES } from 'constants/constants';
import IUser from 'types/IUser';

// Extend the global Window interface to include Chatwoot properties
declare global {
  interface Window {
    chatwootSettings?: {
      hideMessageBubble: boolean;
      position: 'left' | 'right';
      locale: string;
      type: 'expanded_bubble' | 'standard';
      darkMode: 'light' | 'dark' | 'auto';
    };
    chatwootSDK?: {
      run: (config: { websiteToken: string; baseUrl: string }) => void;
    };
    $chatwoot?: {
      setCustomAttributes: (attributes: Record<string, string>) => void;
      setUser: (
        id: string,
        userInfo: {
          email?: string;
          name?: string;
        }
      ) => void;
      reset: () => void;
      toggle: () => void;
    };
  }
}

const useChatwootWidget = ({
  user,
  isLiveChatEnabled,
}: {
  user: IUser | null;
  isLiveChatEnabled: boolean;
}): void => {
  const [chatwootReady, setChatwootReady] = useState(false);

  useEffect(() => {
    if (!isLiveChatEnabled) {
      return () => {};
    }

    // Handler for chatwoot:ready event
    const handleChatwootReady = () => {
      setChatwootReady(true);
    };

    // Listen for chatwoot:ready event
    window.addEventListener('chatwoot:ready', handleChatwootReady);

    // Check if script is already injected
    const existingScript = document.getElementById('chatwoot-script');
    if (existingScript) {
      // If $chatwoot is already available, mark as ready
      if (window.$chatwoot) {
        setChatwootReady(true);
      }
      // Otherwise, the event listener will catch it when ready
      return () => {
        window.removeEventListener('chatwoot:ready', handleChatwootReady);
      };
    }

    const BASE_URL = process.env.REACT_APP_CHATWOOT_BASE_URL;
    const WEBSITE_TOKEN = process.env.REACT_APP_CHATWOOT_WEBSITE_TOKEN;

    // Determine theme
    const isDarkMode =
      document.documentElement.classList.contains(AVAILABLE_THEMES.INDIAN_DARK) ||
      document.documentElement.classList.contains(AVAILABLE_THEMES.DARK);

    // Set chatwootSettings before loading the script
    window.chatwootSettings = {
      hideMessageBubble: true,
      position: 'right',
      locale: 'en',
      type: 'expanded_bubble',
      darkMode: isDarkMode ? 'dark' : 'light',
    };

    const script = document.createElement('script');
    script.id = 'chatwoot-script';
    script.src = `${BASE_URL}/packs/js/sdk.js`;
    script.async = true;
    script.defer = true;

    // Once script is loaded, run Chatwoot
    script.onload = () => {
      if (window.chatwootSDK) {
        window.chatwootSDK.run({
          websiteToken: WEBSITE_TOKEN || '',
          baseUrl: BASE_URL || '',
        });
        // chatwoot:ready event will set chatwootReady state
      } else {
        console.error('Chatwoot SDK not loaded properly');
      }
    };

    script.onerror = () => {
      console.error('Failed to load Chatwoot script');
    };

    document.body.appendChild(script);

    return () => {
      window.removeEventListener('chatwoot:ready', handleChatwootReady);
    };
  }, [isLiveChatEnabled]);

  useEffect(() => {
    if (!chatwootReady || !user?.token || !window.$chatwoot) return;

    window.$chatwoot.setCustomAttributes({
      auth_token: user.token,
    });

    const userObject = {
      email: user.email,
      name: user.firstName ? `${user.firstName} ${user.lastName}` : user.userName || 'NA',
    };

    window.$chatwoot.setUser(String(user.id), userObject);
  }, [
    chatwootReady,
    user?.id,
    user?.token,
    user?.email,
    user?.firstName,
    user?.lastName,
    user?.userName,
  ]);
};

export { useChatwootWidget };
